import React from 'react';
import styled from 'styled-components';
import { Switch, Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import type {
  AirportFilterType,
  AirportsFilterChange,
  SameAirportInboundFilterChange,
  SameAirportOutboundFilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import {
  getFilterInboundSameAirport,
  getFilterOutboundSameAirport,
  mapFilterTypeEventOrigin,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportAirportsFilter';
import { useTransportPageFilterTracking } from '@/app/components/hotel-detail/changeTransport/hooks/useTransportPageFilterTracking';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import useTransportsFetch from '@/app/components/hotel-detail/travel-api/transportsFetch';
import { Icon } from '@/app/common/icons/Icon';
import { mapTransportDistance } from '@/app/components/hotel-detail/common/transportDistance';
import {
  EvoCheckboxRow,
  EvoFilterDivider,
  EvoFilterLoadingLayer,
  EvoFilterSection,
  EvoFilterSectionHeader,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoFilterShared';
import {
  buildAirportInfoByIata,
  groupAirportsByCityAndType,
  hasHotelDistance,
  hasMixedStationTypes,
  shouldShowSameAirportsToggle,
  sortAirportsByDistance,
  toggleIataSelection,
  unionAirportsByIata,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/evoAirportsFilter.utils';

const SameAirportsToggleRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
  padding: 12px 0;
`;

type EvoAirportsFilterProps = {
  inboundArrivalAirports?: AirportFilter[];
  inboundDepartureAirports?: AirportFilter[];
  inboundSameAirport?: SameAirportFilter;
  outboundArrivalAirports?: AirportFilter[];
  outboundDepartureAirports?: AirportFilter[];
  outboundSameAirport?: SameAirportFilter;
  onChange: (
    filterChange: AirportsFilterChange | SameAirportInboundFilterChange | SameAirportOutboundFilterChange
  ) => void;
};

export function EvoAirportsFilter(props: EvoAirportsFilterProps) {
  const {
    inboundArrivalAirports,
    inboundDepartureAirports,
    inboundSameAirport,
    outboundArrivalAirports,
    outboundDepartureAirports,
    outboundSameAirport,
    onChange,
  } = props;

  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  const { data: alternativeTransportsData } = useTransportsFetch();
  const transportDistance = (alternativeTransportsData as FullAvailableTransportResponse)?.distances;
  const distancesData = mapTransportDistance(transportDistance);
  const infoByIata = buildAirportInfoByIata(transportDistance);

  const defaultKind = isTrain ? 'train' : 'airport';

  const showSameAirportsToggle = shouldShowSameAirportsToggle(outboundDepartureAirports, inboundDepartureAirports);
  const originAirports = sortAirportsByDistance(
    unionAirportsByIata(outboundDepartureAirports, inboundArrivalAirports),
    infoByIata
  );
  const destinationAirports = sortAirportsByDistance(
    unionAirportsByIata(outboundArrivalAirports, inboundDepartureAirports),
    infoByIata
  );
  const isMixed = hasMixedStationTypes(
    [...originAirports, ...destinationAirports].map((airport) => airport.iata),
    infoByIata,
    defaultKind
  );

  if (!showSameAirportsToggle && originAirports.length === 0 && destinationAirports.length === 0) return null;

  const onSameAirportsToggle = (enabled: boolean) => {
    const outboundChange = getFilterOutboundSameAirport(
      enabled,
      sendCuriosityClickEvent,
      sendStratrackFlightPageFilterEvent
    );
    const inboundChange = getFilterInboundSameAirport(
      enabled,
      sendCuriosityClickEvent,
      sendStratrackFlightPageFilterEvent
    );
    onChange({
      newState: { ...outboundChange.newState, ...inboundChange.newState },
      event: outboundChange.event,
    });
  };

  const sendAirportTrackingEvents = (type: AirportFilterType, list: AirportFilter[] | undefined, iata: string) => {
    const airport = list?.find((listAirport) => listAirport.iata === iata);
    if (!airport) return;
    const origin = mapFilterTypeEventOrigin[type];
    sendStratrackFlightPageFilterEvent({ filterName: origin, value: iata, deselectFlag: airport.selected });
    sendCuriosityClickEvent({
      event_name: `click_on_${origin}_airport_filters`,
      event_context: 'change_transport_page',
      event_value: iata,
    });
  };

  const onOriginAirportToggle = (iata: string) => {
    const { primary, secondary } = toggleIataSelection(iata, outboundDepartureAirports, inboundArrivalAirports);
    sendAirportTrackingEvents('outboundDepartureAirports', outboundDepartureAirports, iata);
    sendAirportTrackingEvents('inboundArrivalAirports', inboundArrivalAirports, iata);
    onChange({
      newState: {
        ...(primary && { outboundDepartureAirports: primary }),
        ...(secondary && { inboundArrivalAirports: secondary }),
      } as AirportsFilterChange['newState'],
      event: { type: 'FILTER', origin: 'DEPARTURE_AIRPORTS_OUTBOUND_FILTER' },
    });
  };

  const onDestinationAirportToggle = (iata: string) => {
    const { primary, secondary } = toggleIataSelection(iata, outboundArrivalAirports, inboundDepartureAirports);
    sendAirportTrackingEvents('outboundArrivalAirports', outboundArrivalAirports, iata);
    sendAirportTrackingEvents('inboundDepartureAirports', inboundDepartureAirports, iata);
    onChange({
      newState: {
        ...(primary && { outboundArrivalAirports: primary }),
        ...(secondary && { inboundDepartureAirports: secondary }),
      } as AirportsFilterChange['newState'],
      event: { type: 'FILTER', origin: 'ARRIVAL_AIRPORTS_OUTBOUND_FILTER' },
    });
  };

  const getDistanceSubLabel = (iata: string): string | undefined => {
    if (!hasHotelDistance(iata, infoByIata)) return undefined;
    const distance = distancesData.find((distanceData) => distanceData.code === iata)?.distanceWithUnit;
    return distance
      ? `${distance.value} ${distance.unit} ${t('vacation.flightavailability.filter.airportdistance')}`
      : undefined;
  };

  const sameAirportsLabel = isMixed
    ? t('vacation.oss.change_transport.filters.same_locations') || 'Depart/return same locations'
    : isTrain
      ? t('vacation.oss.change_transport.filters.same_stations')
      : t('vacation.oss.change_transport.filters.same_airports');

  // MPH-1204: English fallbacks until smartext-api serves these keys in every language
  const originTitle = t('vacation.oss.change_transport.filters.airports.departure') || 'Departure';
  const destinationTitle = t('vacation.oss.change_transport.filters.airports.destination') || 'Destination';

  const renderRows = (
    side: 'origin' | 'destination',
    airports: AirportFilter[],
    onToggle: (iata: string) => void,
    showDistance: boolean
  ) =>
    airports.map((airport) => (
      <EvoCheckboxRow
        key={airport.iata}
        id={`evo-${side}-airport-${airport.iata}`}
        label={`${airport.name} (${airport.iata})`}
        subLabel={showDistance ? getDistanceSubLabel(airport.iata) : undefined}
        checked={airport.selected}
        disabled={!airport.enabled}
        onChange={() => onToggle(airport.iata)}
      />
    ));

  const renderSide = (
    side: 'origin' | 'destination',
    airports: AirportFilter[],
    sideTitle: string,
    onToggle: (iata: string) => void,
    loadingOrigin: 'DEPARTURE_AIRPORTS_OUTBOUND_FILTER' | 'ARRIVAL_AIRPORTS_OUTBOUND_FILTER'
  ) => {
    const showDistance = side === 'destination';
    return (
      <EvoFilterSection>
        {isMixed ? (
          groupAirportsByCityAndType(airports, infoByIata, defaultKind).map((group, index) => {
            const { city } = group;
            const groupTitle = city
              ? group.kind === 'train'
                ? t('vacation.oss.change_transport.filters.locations.city_train_stations', { city }) ||
                  `${city} train stations`
                : t('vacation.oss.change_transport.filters.locations.city_airports', { city }) || `${city} airports`
              : sideTitle;
            return (
              <React.Fragment key={group.key}>
                {index > 0 && <EvoFilterDivider />}
                <EvoFilterSectionHeader
                  title={groupTitle}
                  icon={
                    group.kind === 'train' ? (
                      <Icon category="Transportation" name="icon-train" size={20} alt="" />
                    ) : (
                      <Icon category="Location" name="icon-airport" size={20} alt="" />
                    )
                  }
                />
                {renderRows(side, group.airports, onToggle, showDistance)}
              </React.Fragment>
            );
          })
        ) : (
          <>
            <EvoFilterSectionHeader title={sideTitle} />
            {renderRows(side, airports, onToggle, showDistance)}
          </>
        )}
        <EvoFilterLoadingLayer origins={[loadingOrigin]} />
      </EvoFilterSection>
    );
  };

  return (
    <>
      {showSameAirportsToggle && (
        <>
          <SameAirportsToggleRow>
            {/* MPH-398: Figma toggle label is Body 16/24 #1e0023 — use Text, not a bare span */}
            <Text variant="Body" color="textPrimary">
              {sameAirportsLabel}
            </Text>
            <Switch
              label=""
              isLabelVisible={false}
              checked={Boolean(outboundSameAirport?.on || inboundSameAirport?.on)}
              onChange={onSameAirportsToggle}
            />
            <EvoFilterLoadingLayer origins={['OUTBOUND_SAME_AIRPORT_FILTER', 'INBOUND_SAME_AIRPORT_FILTER']} />
          </SameAirportsToggleRow>
          <EvoFilterDivider />
        </>
      )}
      {originAirports.length > 0 &&
        renderSide('origin', originAirports, originTitle, onOriginAirportToggle, 'DEPARTURE_AIRPORTS_OUTBOUND_FILTER')}
      {originAirports.length > 0 && destinationAirports.length > 0 && <EvoFilterDivider />}
      {destinationAirports.length > 0 &&
        renderSide(
          'destination',
          destinationAirports,
          destinationTitle,
          onDestinationAirportToggle,
          'ARRIVAL_AIRPORTS_OUTBOUND_FILTER'
        )}
    </>
  );
}
